import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { type CofConfigId, CofNamespace, type ConfigResult, type Value } from "../../gql-generated/types";
import { isDev, isLocal } from "../../shared/constants";
import { type ApolloResult } from "../../shared/models";
import { mapRecord } from "../../shared/utils/object";
import { type GetCofConfigsQuery, type GetCofConfigsQueryVariables, getCofConfigs } from "../api";
import { type CofBaseResult, type ConfigMap, cofConfigIds, cofConfigIdsMap, overridesStore } from "../models";

const emptyConfig: Omit<ConfigResult, "configId" | "value"> = {
    experimentId: 0,
    studyName: "",
    namespace: CofNamespace.MylensesWebCore,
    pb_delete: false,
    priority: 0,
    sequenceId: 0,
    servePlatformsList: [],
};

const emptyValue: Value = {
    boolValue: false,
    floatValue: 0,
    intPairValue: 0,
    intValue: 0,
    longValue: 0,
    stringValue: "",
};

export const useCofConfigs = (): ApolloResult<{ configs: Record<CofConfigId, CofBaseResult> }> => {
    const shouldUseLocalDefaults = isDev() || isLocal();

    // fetches all configs and adds to Apollo cache
    const { data, loading, error } = useQuery<GetCofConfigsQuery, GetCofConfigsQueryVariables>(getCofConfigs, {
        variables: {
            configIds: cofConfigIds,
        },
        skip: shouldUseLocalDefaults,
    });

    const configs = useMemo(
        () =>
            mapRecord(cofConfigIdsMap, (configId): CofBaseResult => {
                if (shouldUseLocalDefaults) {
                    const localStore = overridesStore?.getItem<ConfigMap>();
                    const { type, value } = localStore?.[configId] ?? {};

                    return {
                        ...emptyConfig,
                        configId,
                        value:
                            type === "boolean"
                                ? { ...emptyValue, boolValue: value ?? emptyValue.boolValue }
                                : { ...emptyValue, stringValue: value ?? emptyValue.stringValue },
                        shouldLogExposure: false,
                    };
                }

                const cofConfigs: readonly ConfigResult[] = data?.getCofConfigs.configs ?? [];

                if (loading || !!error || cofConfigs.length === 0) {
                    return {
                        ...emptyConfig,
                        configId,
                        value: emptyValue,
                        shouldLogExposure: false,
                    };
                }

                const configResult = cofConfigs.find((config) => (config.configId as CofConfigId) === configId);
                if (!configResult) {
                    return {
                        ...emptyConfig,
                        configId,
                        value: emptyValue,
                        shouldLogExposure: false,
                    };
                }

                const shouldLogExposure = !!configResult.studyName;
                return { ...configResult, shouldLogExposure };
            }),
        [data, error, loading, shouldUseLocalDefaults],
    );

    return {
        loading,
        error,
        configs,
    };
};
